import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";
import { isEmpty } from "lodash";

import {
  addNewMessage,
  filterEditMessages,
  getArtieLogo,
  getConversationID,
  getIsMaximized,
  getPriceFilter,
  removeAndAddMessages,
  selectLabelItems,
  selectOptionsTypeLogs,
  setIsDisabled,
  setLabelItems,
  setOptionsTypeLogs,
  setReplies,
  updateFaq,
} from "slices";
import MultiInputView from "components/OptionsBox/MultiInputView";
import {
  compMsgKeys,
  defaultEditFormats,
  feedbackKeyOptions,
  formatMessageEdit,
  getNextOptions,
  makeNextOptions,
  optionTypes,
  renderMascot,
  saveConversationFeedback,
} from "utils";
import { Icon } from "components";
import { getPhoneNumberFilter } from "slices/property-slice/index";
import { updateContactMessages } from "slices/messages-slice/index";
import { sendHayleyMessage } from "utils/methods/index";

const ContactForm = ({ unsatisfied, staticImageData, customSize, isLastBot, isUserMsg, position, mascotSettings }) => {
  const dispatch = useDispatch();

  const artieLogo = useSelector(getArtieLogo);
  const isMaximized = useSelector(getIsMaximized);
  const isPriceFilter = useSelector(getPriceFilter);
  const conversationID = useSelector(getConversationID);
  const optionsTypeLogs = useSelector(selectOptionsTypeLogs);
  const prevLabel = useSelector(selectLabelItems);
  const isPhoneRequired = useSelector(getPhoneNumberFilter);
  const [showForm, setShowForm] = useState(true);
  const paramObj = { ...prevLabel };

  const onClick = (messages, nextOptionsType, disablePlaceholerMessage, callbackOptions, data = null) => {
    if (nextOptionsType == "Init") {
      formatMessageEdit("Init");

      return;
    }

    if (defaultEditFormats.includes(nextOptionsType)) {
      dispatch(filterEditMessages([]));
    }

    const msg = messages?.[messages.length - 1];

    sendHayleyMessage({
      text: msg?.message || "",
      queryType: "question",
      ownerType: "prospect",
      kgNodeValue: "Contact me",
    });

    dispatch(addNewMessage(messages));

    if (callbackOptions) {
      const { data, triggerType } = callbackOptions;

      switch (triggerType) {
        case "SAVE_2_LOCAL_STORAGE":
          paramObj[data.name] = data.data;
          dispatch(setLabelItems(paramObj));
          break;

        default:
          break;
      }
    }

    if (nextOptionsType) {
      if (!disablePlaceholerMessage) {
        setTimeout(() => {
          const message = optionTypes[nextOptionsType] || `typing`;
          dispatch(addNewMessage([{ message: message, speaker: "bot", createdAt: new Date().toISOString() }]));
        }, 300);
      }

      // Disable options clicking
      dispatch(setIsDisabled(true));

      if (["LikeTour", "DisLikeTour", "SkipTour", "LikeContact", "DisLikeContact", "SkipContact"].includes(nextOptionsType)) {
        saveConversationFeedback(conversationID, data, feedbackKeyOptions[nextOptionsType]);
        dispatch(filterEditMessages([]));
      }

      const handleNextOptionSuccess = (res) => {
        const { nextReplies, nextBotMessages } = makeNextOptions(nextOptionsType, res.data, res.extraData || undefined);

        let newMessages = nextBotMessages || [];

        if (["unitView"].includes(nextReplies?.renderType)) {
          newMessages = [{ key: compMsgKeys[nextReplies?.renderType] || nextReplies?.renderType, speaker: "comp", replies: nextReplies }];
        }

        if (!disablePlaceholerMessage) {
          setTimeout(() => {
            dispatch(removeAndAddMessages(newMessages));
          }, 300);
        } else {
          dispatch(addNewMessage(newMessages));
        }

        const msg = messages?.[messages.length - 1];

        sendHayleyMessage({
          text: msg?.message || "",
          queryType: "answer",
          ownerType: "agent",
          kgNodeValue: "Contact me",
        });

        dispatch(setReplies(nextReplies));
        dispatch(setIsDisabled(false));

        if (nextOptionsType !== replies.optionsType) {
          dispatch(setOptionsTypeLogs([...optionsTypeLogs, replies.optionsType]));
        }

        if (!isPriceFilter && ["PriceBrackets"].includes(nextOptionsType)) {
          if (nextReplies.options.length) {
            const { nextOptionsType: type, data: getData } = nextReplies.options[0];

            getNextOptions(type, getData).then((res) => {
              const { nextReplies } = makeNextOptions(type, res.data, res.extraData || undefined);

              dispatch(setReplies(nextReplies));
            });
          }
        }
      };

      getNextOptions(nextOptionsType, data)
        .then((res) => {
          handleNextOptionSuccess(res);
        })
        .catch(async (err) => {})
        .finally(() => {
          dispatch(setIsDisabled(false));
          setShowForm(false);
          dispatch(updateContactMessages());
        });
    }
  };

  const handleCancel = () => {
    sendHayleyMessage({
      text: "Cancel",
      queryType: "question",
      ownerType: "prospect",
      kgNodeValue: "Contact me",
    });

    dispatch(addNewMessage([{ message: "cancel", speaker: "user", createdAt: new Date().toISOString() }]));
    dispatch(addNewMessage([{ message: "howHelp", speaker: "bot", createdAt: new Date().toISOString() }]));
    setShowForm(false);
    dispatch(updateFaq(true));
    dispatch(updateContactMessages());

    sendHayleyMessage({
      text: t("howHelp") || "",
      queryType: "answer",
      ownerType: "agent",
      kgNodeValue: "Contact me",
    });
  };

  const replies = {
    checkStorage: true,
    disableResetBox: false,
    options: [
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "firstName",
            type: "text",
            label: "firstName",
            officialLabel: "first name",
            validate: "TEXT_ONLY",
            callbackOptions: null,
          },
        },
        message: [],
        nextOptionsType: "PostContactUs",
        key: "first_name",
        isRequired: true,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "lastName",
            type: "text",
            label: "lastName",
            officialLabel: "last name",
            validate: "TEXT_ONLY",
            callbackOptions: null,
          },
        },
        message: [],
        nextOptionsType: "PostContactUs",
        key: "last_name",
        isRequired: true,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "email",
            type: "email",
            label: "email",
            officialLabel: "email",
            validate: "EMAIL",
            callbackOptions: null,
          },
        },
        message: [],
        nextOptionsType: "PostContactUs",
        key: "email",
        isRequired: true,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "phoneNumber",
            type: "text",
            label: "phoneNumber",
            officialLabel: "phone number",
            validate: "PHONE",
            callbackOptions: null,
            isOptional: isPhoneRequired ? false : true,
          },
        },
        nextOptionsType: "PostContactUs",
        message: [],
        key: "phone",
        isRequired: isPhoneRequired ? true : false,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
    ],
    optionsType: "Form4ContactUs",
    title: "Enter First Name and Last Name",
    renderType: "multiInputView",
    bareMinimum: true,
  };

  useEffect(() => {
    !unsatisfied && dispatch(updateFaq(false));
  }, []);

  return (
    <div className="hayley-mt-4">
      <div style={{ overflowWrap: "break-word" }}>
        <div className={`hayley-flex ${isLastBot && (isMaximized ? "hayley-mb-4" : "hayley-mb-3")}`}>
          {isUserMsg ? (
            isMaximized ? (
              <Icon iconType="DefaultUser" classes={"hayley-h-9 hayley-w-9 hayley-object-cover"} />
            ) : (
              ""
            )
          ) : (
            <>
              {isEmpty(artieLogo) && <div className="hayley-h-9 hayley-w-9">{renderMascot(mascotSettings, position, true, customSize, false)}</div>}

              {!isEmpty(artieLogo) && (
                <div className="hayley-z-10 hayley-flex hayley-h-9 hayley-w-9 hayley-items-center hayley-justify-center hayley-rounded-full hayley-border hayley-border-[#EEE]">
                  <img
                    id="staticImage"
                    src={artieLogo.includes("data:image/gif") ? staticImageData : artieLogo}
                    alt=""
                    className={`hayley-h-8 hayley-w-8 hayley-rounded-full hayley-object-cover`}
                  />
                </div>
              )}
            </>
          )}
          <div>
            <div className="hayley-mx-3 hayley-max-w-[725px] hayley-rounded-lg hayley-rounded-tl-none hayley-bg-custom-7 !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary">
              {`${
                unsatisfied
                  ? Object.keys(paramObj).length > 0
                    ? "One of our agents will personally respond to your question on following contact details:"
                    : "I’m sorry about that. Please fill in the details below and one of my colleagues will reach out with a response."
                  : Object.keys(paramObj).length > 0
                  ? "One of our agents will personally respond to your question on following contact details:"
                  : "Please fill in the details below and I will get you in touch with the right person."
              }`}
            </div>
            {showForm && (
              <div className="hayley-mx-3 hayley-mt-4">
                <MultiInputView replies={replies} onClick={onClick} onCancel={handleCancel} />
              </div>
            )}
            {unsatisfied && (
              <div
                className="hayley-mx-3 hayley-mt-4 hayley-max-w-[725px] hayley-rounded-lg hayley-rounded-tl-none hayley-bg-custom-7 !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary"
                style={{ overflowWrap: "break-word" }}
              >
                You can continue asking me more about the property while waiting for a response from our agent
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
